/**
 * Pinned processing result for the Car Emblem practice design.
 *
 * Colour detection runs through Gemini, which returns its palette in no
 * particular order, and that order becomes the layer order in the Stitch
 * Editor. The walkthrough says "click layer 2 — the pink", so a reshuffle
 * sends the learner to the wrong swatch. Tutorial designs therefore skip
 * detection and use a fixed palette instead.
 *
 * The artwork is two colours over a transparent background — white 51% of the
 * opaque pixels, pink 49%. White goes first, which is also the sensible stitch
 * order: the panel behind, then the emblem on top of it.
 */
export const palette = [
  { hex: 'ffffff', label: 'white panel' },
  { hex: 'f480df', label: 'pink emblem' },
];

/** Shape `processAndAnalyzeImage` returns: `[hexWithoutHash, enabled]`. */
export const colors = palette.map(({ hex }) => [hex, true]);

/**
 * One-based layer numbers, matching the "Select layer N" controls.
 * The lesson's copy and its step targets are both built from these, so the two
 * can't drift apart if the palette order is ever changed.
 */
export const layers = {
  panel: 1,
  emblem: 2,
};

/**
 * Pinned stitch-settings analysis.
 *
 * Normally a model picks a starting stitch type per layer, which makes the
 * lesson different every time — and if it happened to choose satin for the
 * pink, the walkthrough's "Replace this layer" step would have nothing to
 * replace. Both layers therefore start as fills, which is what makes the
 * conversion to satin an actual change the learner performs.
 *
 * `index` is zero-based and lines up with `palette`.
 */
export const aiAnalysis = {
  layers: [
    {
      index: 0,
      description: 'The white panel inside the emblem',
      recommended_stitch_type: 'auto_fill',
      angle: 0,
      row_spacing_mm: 0.4,
      max_stitch_length_mm: 2.8,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'Broad open areas stitch cleanly as a standard fill.',
    },
    {
      index: 1,
      description: 'The pink emblem — an outer ring and two crossed ovals',
      recommended_stitch_type: 'auto_fill',
      angle: 0,
      row_spacing_mm: 0.35,
      max_stitch_length_mm: 2.6,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'Starts as a fill — the walkthrough cuts it apart and rebuilds it as satin.',
    },
  ],
  global: {
    suggested_density: 'normal',
    estimated_complexity: 'moderate',
    warnings: [],
    tips: [
      'Stitch the white panel first, then the pink emblem on top.',
      'Grow the white slightly so no fabric shows along the pink.',
    ],
  },
  machine_recommendations: {
    stabilizer: {
      type: 'cut-away',
      weight: 'medium',
      reason: 'Long satin bands pull the fabric and need stable backing.',
    },
    needle: {
      type: 'universal',
      size: '75/11',
      reason: 'Suits standard 40wt rayon or polyester thread.',
    },
    tension: { top: 'normal', bobbin: 'normal', notes: 'Standard settings are fine for this design.' },
    speed: 'medium',
    hooping: 'Hoop the fabric and stabiliser together, drum-tight.',
  },
};

const fixture = { palette, colors, layers, aiAnalysis };

export default fixture;
